import React, { useEffect, useMemo, useState } from 'react';
import { View, ScrollView, Pressable, StyleSheet, Platform, ActivityIndicator } from 'react-native';
import { useRouter } from 'expo-router';
import { useTheme } from '@/context/ThemeContext';
import { useTranslation } from '@/hooks/useTranslation';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import ToolFlagBadges from '@/components/Tools/ToolFlagBadges';
import ToolAvatar from '@/components/Tools/ToolAvatar';
import { toolsApi } from '@/lib/api';
import { getPaletteForTool } from '@/components/Tools/toolDetailHelpers';

/**
 * Horizontal "Related tools" rail rendered near the bottom of the tool
 * detail page. Backed by `GET /json/tools/:slug/related`, which ranks
 * sibling tools by category > shared tags > shared outputType.
 *
 * Renders nothing while loading or if the API returns no related tools so
 * empty states never collapse with awkward whitespace on the page.
 */
/**
 * @param {object}   props
 * @param {string}   props.slug         source tool/prompt slug
 * @param {Function} [props.loadRelated] async (slug, { limit }) => { tools: [...] }.
 *                                        Defaults to sibling tools; pass
 *                                        `promptsApi.relatedTools` to surface
 *                                        the tools related to a prompt.
 * @param {string}   [props.title]      header label override
 */
function ToolRelatedTools({ slug, loadRelated, title }) {
  const { colors } = useTheme();
  const { t } = useTranslation();
  const router = useRouter();
  const [tools, setTools] = useState(null); // null = loading, [] = empty, [...] = ready
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!slug) return;
    let cancelled = false;
    setTools(null);
    setError(null);
    (async () => {
      try {
        const loader = loadRelated || ((s, opts) => toolsApi.related(s, opts));
        const data = await loader(slug, { limit: 8 });
        if (cancelled) return;
        setTools(Array.isArray(data?.tools) ? data.tools : []);
      } catch (e) {
        if (cancelled) return;
        setError(e?.message || 'Failed to load related tools');
        setTools([]);
      }
    })();
    return () => { cancelled = true; };
  }, [slug, loadRelated]);

  const styles = useMemo(() => StyleSheet.create({
    wrap: {
      gap: 12,
    },
    headerRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
    },
    scroll: {
      marginHorizontal: -24,
    },
    scrollContent: {
      flexDirection: 'row',
      gap: 12,
      paddingHorizontal: 24,
      paddingVertical: 4,
    },
    card: {
      width: 240,
      padding: 14,
      borderRadius: 14,
      borderWidth: 1,
      borderColor: colors.border_color_secondary,
      backgroundColor: colors.solid_color_block,
      gap: 10,
      ...(Platform.OS === 'web' ? {
        cursor: 'pointer',
        transition: 'border-color 0.15s ease, background-color 0.15s ease',
      } : null),
    },
    cardHovered: {
      backgroundColor: colors.solid_color_tertiary,
    },
    cardHeader: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 10,
    },
    iconWrap: {
      width: 32,
      height: 32,
      borderRadius: 8,
      alignItems: 'center',
      justifyContent: 'center',
    },
    badgeRow: {
      flexDirection: 'row',
      gap: 4,
      marginLeft: 'auto',
    },
    desc: {
      lineHeight: 18,
    },
    metaRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 6,
      marginTop: 'auto',
    },
    loaderRow: {
      paddingVertical: 18,
      alignItems: 'center',
    },
    sectionTitle: {
      marginBottom: 10,
    },
  }), [colors]);

  if (tools && !tools.length && !error) return null; // hide section entirely
  if (error && !tools?.length) return null;

  return (
    <View style={styles.wrap}>
      <View style={styles.headerRow}>
        <Text tag="h2" size="xxxl" weight="bold" color="primary" style={styles.sectionTitle}>
          {title || t('pages.tools.relatedTools') || 'Related tools'}
        </Text>
      </View>

      {tools == null ? (
        <View style={styles.loaderRow}>
          <ActivityIndicator size='small' color={colors.text_color_secondary} />
        </View>
      ) : (
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          style={styles.scroll}
          contentContainerStyle={styles.scrollContent}
        >
          {tools.map(tool => (
            <RelatedToolCard
              key={tool.id || tool.slug}
              tool={tool}
              colors={colors}
              styles={styles}
              onPress={() => router.push(`/tools/${tool.slug}`)}
            />
          ))}
        </ScrollView>
      )}
    </View>
  );
}

function RelatedToolCard({ tool, colors, styles, onPress }) {
  const palette = getPaletteForTool(tool);

  return (
    <Pressable
      onPress={onPress}
      accessibilityRole='link'
      accessibilityLabel={tool?.name}
      style={({ hovered }) => [
        styles.card,
        hovered && [styles.cardHovered, { borderColor: palette.fg }],
      ]}
    >
      <View style={styles.cardHeader}>
        <ToolAvatar
          tool={tool}
          size={32}
          iconSize={16}
          borderRadius={8}
          backgroundColor={palette.bg}
          color={palette.fg}
        />
        {(tool?.flags?.isFeatured || tool?.flags?.isNew || tool?.flags?.isBeta) && (
          <View style={styles.badgeRow}>
            <ToolFlagBadges tool={tool} />
          </View>
        )}
      </View>

      <Text size='n' weight='bold' color='primary' numberOfLines={1}>
        {tool?.name}
      </Text>
      {!!tool?.description && (
        <Text size='xs' color='secondary' numberOfLines={2} style={styles.desc}>
          {tool.description}
        </Text>
      )}

      <View style={styles.metaRow}>
        {!!tool?.category?.name && (
          <Text size='xxs' color='tertiary'>
            {tool.category.name}
          </Text>
        )}
        {!!tool?.outputType && (
          <>
            <Text size='xxs' color='tertiary'>·</Text>
            <Text size='xxs' color='tertiary'>{tool.outputType}</Text>
          </>
        )}
      </View>
    </Pressable>
  );
}

export default React.memo(ToolRelatedTools);
